import React, { useCallback, useMemo, useRef, useState, memo } from "react";
import { useLocation } from "react-router-dom";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { usePrepareWorkspaceCheckoutMutation } from "@api/flatRateBilling.api";
import { useCanManageWorkspaceBilling } from "@hooks/permissions/useCanManageWorkspaceBilling";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { useScope } from "@state/scopeContext.lib";
import { trackEvent } from "@lib/analytics";
import { ChildrenProps } from "../../../types";
import {
    planManagementContext,
    planManagementModalContext,
    PlanManagementContextValue,
    PlanManagementModalContextValue,
    UpgradeModalCustomizationOptions,
    UpgradeModalParams,
} from "./PlanManagementContext";
import { PlanManagementModals } from "./PlanManagementModals";

interface PlanManagementProviderProps extends ChildrenProps {
    onBeforeUpgrade?(plan: FLAT_RATE_PLANS, trial: boolean): void;
    onBeforeDowngrade?(plan: FLAT_RATE_PLANS): void;
}

export const PlanManagementProvider = memo(
    ({
        children,
        onBeforeDowngrade,
        onBeforeUpgrade,
    }: PlanManagementProviderProps) => {
        const canManageBilling = useCanManageWorkspaceBilling();
        const { isSubscribed } = useBillingStatus();
        const location = useLocation();
        const { workspaceId } = useScope();
        const { mutateAsync: prepareCheckout, data: checkoutData } =
            usePrepareWorkspaceCheckoutMutation();

        const beforeUpgrade: PlanManagementProviderProps["onBeforeUpgrade"] =
            useMemo(
                () =>
                    onBeforeUpgrade ??
                    ((plan) => {
                        void trackEvent(
                            isSubscribed
                                ? AnalyticsEvent.PLAN_UPGRADE_INITIATED
                                : AnalyticsEvent.TRIAL_START_INITIATED,
                            {
                                product: plan,
                                path: location.pathname,
                            },
                        );
                    }),
                [isSubscribed, location.pathname, onBeforeUpgrade],
            );

        const beforeDowngrade: PlanManagementProviderProps["onBeforeDowngrade"] =
            useMemo(
                () =>
                    onBeforeDowngrade ??
                    ((plan) => {
                        void trackEvent(
                            AnalyticsEvent.PLAN_DOWNGRADE_INITIATED,
                            {
                                product: plan,
                                path: location.pathname,
                            },
                        );
                    }),
                [location.pathname, onBeforeDowngrade],
            );

        const [downgradeModal, setDowngradeModal] =
            useState<FLAT_RATE_PLANS | null>(null);

        const [upgradeModal, setUpgradeModal] =
            useState<UpgradeModalParams | null>(null);

        const [convertModal, setConvertModal] = useState<boolean>(false);

        const openUpgradeModal = useCallback(
            async (
                plan: FLAT_RATE_PLANS,
                isTrial: boolean,
                options: UpgradeModalCustomizationOptions = {},
            ) => {
                if (!workspaceId) {
                    return;
                }
                beforeUpgrade(plan, isTrial);
                if (canManageBilling && !checkoutData) {
                    await prepareCheckout(workspaceId);
                }
                setUpgradeModal({ plan, isTrial, ...options });
            },
            [
                beforeUpgrade,
                canManageBilling,
                workspaceId,
                prepareCheckout,
                checkoutData,
            ],
        );

        const openDowngradeModal = useCallback(
            (plan: FLAT_RATE_PLANS) => {
                beforeDowngrade(plan);
                setDowngradeModal(plan);
            },
            [beforeDowngrade],
        );

        const openConvertModal = useCallback(async () => {
            if (!workspaceId) {
                return;
            }
            if (canManageBilling && !checkoutData) {
                await prepareCheckout(workspaceId);
            }
            setConvertModal(true);
        }, [canManageBilling, prepareCheckout, workspaceId, checkoutData]);

        const value: PlanManagementContextValue = useMemo(
            () => ({
                downgrade: openDowngradeModal,
                upgrade: openUpgradeModal,
                convert: openConvertModal,
            }),
            [openConvertModal, openDowngradeModal, openUpgradeModal],
        );

        const handleUpgradeModalHide = useCallback(() => {
            if (upgradeModal) {
                void trackEvent(AnalyticsEvent.ONBOARDING_PAYMENT_CANCELED, {
                    plan: upgradeModal.plan,
                });
                upgradeModal.onHide?.();
            }
            setUpgradeModal(null);
        }, [upgradeModal]);

        const onHideDowngrade = useCallback(() => setDowngradeModal(null), []);

        const onHideConvert = useCallback(() => setConvertModal(false), []);

        const hostIdsRef = useRef<string[]>([]);
        const hostListenersRef = useRef<Set<() => void>>(new Set());

        const notifyHostListeners = useCallback(() => {
            hostListenersRef.current.forEach((listener) => listener());
        }, []);

        const registerAsModalHost = useCallback(
            (id: string) => {
                hostIdsRef.current = [...hostIdsRef.current, id];
                notifyHostListeners();
                return () => {
                    hostIdsRef.current = hostIdsRef.current.filter(
                        (hostId) => hostId !== id,
                    );
                    notifyHostListeners();
                };
            },
            [notifyHostListeners],
        );

        const subscribeToHostChanges = useCallback((listener: () => void) => {
            hostListenersRef.current.add(listener);
            return () => hostListenersRef.current.delete(listener);
        }, []);

        const getActiveHostId = useCallback(
            () => hostIdsRef.current[hostIdsRef.current.length - 1] ?? null,
            [],
        );

        const modalValue: PlanManagementModalContextValue = useMemo(
            () => ({
                upgradeModal,
                downgradeModal,
                convertModal,
                checkoutData,
                onHideUpgrade: handleUpgradeModalHide,
                onHideDowngrade,
                onHideConvert,
                registerAsModalHost,
                subscribeToHostChanges,
                getActiveHostId,
            }),
            [
                upgradeModal,
                downgradeModal,
                convertModal,
                checkoutData,
                handleUpgradeModalHide,
                onHideDowngrade,
                onHideConvert,
                registerAsModalHost,
                subscribeToHostChanges,
                getActiveHostId,
            ],
        );

        return (
            <planManagementContext.Provider value={value}>
                <planManagementModalContext.Provider value={modalValue}>
                    <PlanManagementModals />
                    {children}
                </planManagementModalContext.Provider>
            </planManagementContext.Provider>
        );
    },
);
